/**
 * adsProducts.ts — the one fact sheet per product and the one set of standing rules every ad-writing step reads.
 *
 * Source: the Ads automation handoff (Oct 22, 2026). Anything that is not on a fact sheet cannot appear in an ad.
 * The rules here are used twice: `adsChecks.ts` enforces them in code, and `renderRulesForModel` turns the same data
 * into the text that Claude and Grok are given, so the two can never drift apart.
 */
import { z } from "zod";

export const AD_PRODUCT_IDS = ["sonic_hire", "raya"] as const;
export const adProductIdSchema = z.enum(AD_PRODUCT_IDS);
export type AdProductId = z.infer<typeof adProductIdSchema>;

/** Channels are modules: a new one (Meta) is one more entry here plus its own checks and publisher. */
export const AD_CHANNELS = ["linkedin", "google_search"] as const;
export const adChannelSchema = z.enum(AD_CHANNELS);
export type AdChannel = z.infer<typeof adChannelSchema>;
export const AD_CHANNEL_LABEL: Record<AdChannel, string> = { linkedin: "LinkedIn", google_search: "Google Search" };

export interface AdProduct {
  id: AdProductId;
  name: string;
  site: string;
  whatItIs: string;
  coreTerm: string;
  buyers: string;
  notAudience: string;
  mainCompetitor: { name: string; note: string };
  googleCampaign: string;
  keyPages: string;
  mainCta: string;
  hasLinkedInCompanyPage: boolean;
  markets: string;
  /** The only things an ad may state as fact about the product. */
  facts: readonly string[];
  /** Words and phrases that must never appear in copy for this product, with the reason shown to the writer. */
  banned: ReadonlyArray<{ pattern: RegExp; reason: string }>;
  /** Words that are buyer intent here and must never be one-word negative keywords. */
  protectedKeywordWords: readonly string[];
  /** Names that must not appear in ad copy unless written approval exists. */
  protectedNames: readonly string[];
  /** Product-specific copy rules, as sentences for the model. */
  messagingRules: readonly string[];
  /** On-image visuals the brand allows (at most one). */
  imageVisuals: readonly string[];
  /** LinkedIn groups this product may use. */
  linkedInGroups: readonly LinkedInGroup[];
}

export const LINKEDIN_GROUPS = ["company_page", "team_member"] as const;
export const linkedInGroupSchema = z.enum(LINKEDIN_GROUPS);
export type LinkedInGroup = z.infer<typeof linkedInGroupSchema>;
export const LINKEDIN_GROUP_LABEL: Record<LinkedInGroup, string> = { company_page: "Company page", team_member: "Team member" };

const SONIC_HIRE_FACTS = [
  "Browser-link voice interview on the candidate's own time",
  "The AI asks follow-up questions",
  "No video avatar, meeting links or downloads",
  "Rubric weightings and AI-drafted questions from a job description",
  "Transcripts, recordings and scores",
  "Same questions and rubric for everyone",
  "Logged attempts, with approval for reattempts",
  "Teammates, notes and tags",
  "Optional automations",
  "Replaces the 15 to 30 minute first-round phone screen",
  "Start free with 10 free interviews, no credit card",
  "Monthly or annual billing (annual saves 20%)",
  "No per-seat pricing or minimum commitment",
  "Candidate consent",
  "Never clones voices",
  "Connects to your ATS",
] as const;

const RAYA_FACTS = [
  "Greets callers in the clinic's name and answers common questions",
  "Logs who called and why, with a transcript and one-click callback",
  "Follow-up text to every caller (including the clinic's own booking link if it has one)",
  "Auto-text for missed calls and voicemails",
  "Drafts answers from the clinic's website (up to 10 pages, re-checked weekly) for staff approval",
  "3-step setup",
  "Can ring the front desk first",
  "Transfers callers who ask for a person",
  "Hands medical, pricing and insurance questions to the team",
  "Works alongside Jane App and other practice software",
  "HIPAA-compliant with audit logging, redaction by default, role-based access and two-step sign-in",
  "Early access, no public pricing or free trial",
] as const;

/** Raya features that are not available yet. Never presented as available. */
export const RAYA_COMING_SOON = ["booking", "reminders", "confirmations", "waitlist", "recall", "intake forms", "group messages", "payments", "no-show", "patient import"] as const;

export const AD_PRODUCTS: Record<AdProductId, AdProduct> = {
  sonic_hire: {
    id: "sonic_hire",
    name: "SonicHire",
    site: "sonichire.ai",
    whatItIs: "AI recruiter for first-round interviews: async voice interviews of qualified candidates and a ranked shortlist; the hiring team makes the final call.",
    coreTerm: "AI interviewer",
    buyers: "Recruiters, talent acquisition, HR, hiring managers, founders hiring at volume (sales/SDR, operations, field roles, agencies)",
    notAudience: "Job seekers; anyone searching for deepfakes",
    mainCompetitor: { name: "CoRecruit", note: "corecruit.com (pinned main LinkedIn competitor)" },
    googleCampaign: "Sonic Hire | Search | US+CA",
    keyPages: '/ (H1 "AI Recruiter for First-Round Interviews"), /pricing, /earlyaccess, /about, /blog',
    mainCta: "Get Early Access",
    hasLinkedInCompanyPage: true,
    markets: "US and Canada",
    facts: SONIC_HIRE_FACTS,
    banned: [
      { pattern: /\breplac(?:e|es|ed|ing)\s+(?:your\s+|the\s+|a\s+)?(?:recruiters?|hr|talent)\b/i, reason: "Never imply SonicHire replaces recruiters. Say it removes first-round grunt work." },
      { pattern: /\b(?:screens?|interviews?)\s+(?:every(?:one|body)|all\s+(?:of\s+)?(?:your\s+)?(?:candidates|applicants))\b/i, reason: "Never say it screens or interviews everyone. Talk about qualified candidates and the ranked shortlist." },
      { pattern: /\bnever\s+rejects?\b/i, reason: 'Never say it "never rejects anyone".' },
      { pattern: /\ba\s+person\s+makes\s+every\s+call\b/i, reason: 'Say it to the buyer: "You Make the Final Call" or "You Get a Ranked List".' },
      { pattern: /\bdeep\s?fakes?\b/i, reason: "No deepfake-related copy or keywords." },
    ],
    protectedKeywordWords: ["hire", "hiring", "recruit", "interview", "screening", "candidate", "shortlist", "free", "pricing", "software", "best", "alternative", "vs", "ai"],
    protectedNames: ["CoRecruit", "Omar Qureshi", "Neuron"],
    messagingRules: [
      "Never imply SonicHire replaces recruiters. Frame it as removing first-round grunt work so recruiters spend time on top candidates and the second-round decision.",
      'Never say it screens or interviews everyone, or "never rejects anyone". Talk about qualified candidates, the ranked shortlist and the hiring team choosing who advances.',
      'No vague human-in-the-loop lines like "A Person Makes Every Call". Say it to the buyer: "You Make the Final Call", "You Get a Ranked List".',
      "No deepfake-related copy or keywords.",
    ],
    imageVisuals: ["a ranked-shortlist card", "a voice waveform", "none"],
    linkedInGroups: ["company_page", "team_member"],
  },
  raya: {
    id: "raya",
    name: "Raya",
    site: "meetraya.com",
    whatItIs: "AI receptionist for healthcare clinics: answers calls, texts and website chat 24/7 or after hours.",
    coreTerm: "AI receptionist",
    buyers: "Clinic owners, practice managers, front-desk leads (physio, chiro, dental, medical, wellness)",
    notAudience: "Patients; job seekers",
    mainCompetitor: { name: "Hello Patient", note: "pinned on LinkedIn. Google Ads: not pinned" },
    googleCampaign: "Raya | Search | US+CA",
    keyPages: '/ (H1 "Never miss a patient. Even after hours."), /early-access',
    mainCta: "Get early access",
    hasLinkedInCompanyPage: false,
    markets: "US and Canada",
    facts: RAYA_FACTS,
    banned: [
      { pattern: /\bphipa\b/i, reason: "No compliance claims beyond HIPAA as stated on the site. No PHIPA claim." },
      { pattern: /\b(?:cures?|cured|treats?|treating|treatment|diagnos(?:e|es|ed|is|ing)|heals?|therapeutic)\b/i, reason: "No medical claims." },
    ],
    protectedKeywordWords: ["receptionist", "answering service", "phone", "calls", "after hours", "24/7", "clinic", "practice", "medical", "dental", "healthcare", "front desk", "virtual", "ai", "software", "pricing", "free", "best", "alternative", "vs", "near me", "hipaa"],
    protectedNames: ["Hello Patient"],
    messagingRules: [
      `Never present coming-soon features as available: ${RAYA_COMING_SOON.join(", ")}. In Google copy, avoid the words booking, reminders, waitlist, payments and no-show entirely.`,
      "No medical claims, and no compliance claims beyond HIPAA as stated on the site (no PHIPA claim).",
      "Small and solo practices are one angle, at most about a third of each week's concepts or ad groups.",
      "Raya has no LinkedIn company page yet, so every Raya LinkedIn concept is Team member.",
    ],
    imageVisuals: ["a missed-call notification", "a follow-up text bubble", "a call log", "none"],
    linkedInGroups: ["team_member"],
  },
};

export const productOf = (id: AdProductId): AdProduct => AD_PRODUCTS[id];

// --------------------------------------------------------------------------------------------------------------------
// Limits and standing rules (one place, read by the checks and by the model prompts)
// --------------------------------------------------------------------------------------------------------------------

export const RSA_LIMITS = {
  headline: 30,
  description: 90,
  descriptionAimMin: 61,
  descriptionAimMax: 70,
  path: 15,
  sitelinkText: 25,
  sitelinkDescription: 35,
  callout: 25,
  maxHeadlines: 15,
  maxDescriptions: 4,
  maxSitelinks: 4,
  maxCallouts: 8,
  maxSnippets: 2,
  maxExclamationsInDescriptions: 1,
} as const;

export const IMAGE_RULES = {
  hookMinWords: 3,
  hookMaxWords: 8,
  highlightMaxWords: 2,
  sizes: [
    { id: "square", width: 1080, height: 1080 },
    { id: "landscape", width: 1200, height: 627 },
  ],
} as const;

/** Words that need proof on the site before an ad may use them. */
export const PROOF_WORDS = ["#1", "best", "top", "cheapest", "guaranteed"] as const;

export const COPY_RULES: readonly string[] = [
  "Real facts only. Use only facts from the product's fact sheet or from the research rows. Never invent numbers, customers, reviews or features.",
  'Be realistic, never exaggerated. "8 months of a recruiter\'s time" is fine; "a recruiter doing nothing else" is not.',
  "Never name or tag other companies as sources in ad copy. Research several sources and use an averaged figure instead. Never put a competitor's brand in ads, and never reuse a competitor's hook wording.",
  "Customer names, logos or testimonial numbers only with Sara's approval and the customer's written OK.",
];

export const LINKEDIN_RULES: readonly string[] = [
  'Scroll-stopping, opinionated hook, then one connected narrative that introduces the product to a cold audience (the model is the "$300 vs $99" post style).',
  "One concept carried through each post. Avoid obvious or purely logical lines, long disconnected text and long bullets.",
  "Format is always post text plus one image: no PDFs, document ads, carousels or video.",
  "Two ad groups: Company page ads, and Team member posts promoted as thought leader ads.",
];

export const GOOGLE_RULES: readonly string[] = [
  `Headlines max ${RSA_LIMITS.headline} characters, descriptions max ${RSA_LIMITS.description} (aim ${RSA_LIMITS.descriptionAimMin} to ${RSA_LIMITS.descriptionAimMax}), paths max ${RSA_LIMITS.path}, sitelink text and callouts max ${RSA_LIMITS.sitelinkText}, sitelink descriptions max ${RSA_LIMITS.sitelinkDescription}.`,
  `No exclamation marks in headlines, at most ${RSA_LIMITS.maxExclamationsInDescriptions} across all descriptions. No ALL CAPS, emphasis symbols or phone numbers.`,
  'No "#1", "best", "top", "cheapest" or "guaranteed" without proof on the site. Talk to the buyer ("you"), not "we".',
  "Exact and Phrase match only, never Broad. Headlines 1 to 3 are keyword variants pinned to position 1.",
];

export const IMAGE_RULE_TEXT: readonly string[] = [
  `The image supports the post; it is not the ad. One short on-image hook of ${IMAGE_RULES.hookMinWords} to ${IMAGE_RULES.hookMaxWords} words in large type, with one or two words highlighted. No paragraphs, subheads or CTAs on the image.`,
  "It must make sense on its own: at a glance it is clearly an AI interview tool for recruiters (or an AI receptionist for clinics).",
  "At most one supporting visual. No meaningless sample numbers on mock-ups.",
  "Built from brand templates, so the words and logo render exactly. An AI picture may be used only as the background, and it never contains words, logos or screens. Sizes: 1080×1080 square and 1200×627 landscape.",
];

/**
 * The text both models receive. Built from the data above, so adding a banned phrase or a fact changes what Claude and
 * Grok are told at the same moment it changes what the code enforces.
 */
export function renderRulesForModel(product: AdProductId, channel: AdChannel): string {
  const p = AD_PRODUCTS[product];
  const lines: string[] = [
    `PRODUCT: ${p.name} (${p.site}). ${p.whatItIs}`,
    `Core term: ${p.coreTerm}. Buyers: ${p.buyers}. Not the audience: ${p.notAudience}. Markets: ${p.markets}. Main call to action: ${p.mainCta}.`,
    "",
    "FACTS YOU MAY STATE (nothing else about the product may appear in an ad):",
    ...p.facts.map((f) => `- ${f}`),
    "",
    "RULES FOR ALL COPY:",
    ...COPY_RULES.map((r) => `- ${r}`),
    ...p.messagingRules.map((r) => `- ${r}`),
    `- Never mention these names in ad copy: ${[p.mainCompetitor.name, ...p.protectedNames].filter((v, i, a) => a.indexOf(v) === i).join(", ")}.`,
    "",
    channel === "linkedin" ? "RULES FOR LINKEDIN ADS:" : "RULES FOR GOOGLE SEARCH ADS:",
    ...(channel === "linkedin" ? LINKEDIN_RULES : GOOGLE_RULES).map((r) => `- ${r}`),
  ];
  if (channel === "linkedin") {
    lines.push("", "RULES FOR THE IMAGE:", ...IMAGE_RULE_TEXT.map((r) => `- ${r}`), `- Allowed supporting visuals: ${p.imageVisuals.join("; ")}.`);
  }
  return lines.join("\n");
}

/** What a search-term check says about a negative keyword. Lives here (a leaf module) so draft and check modules can both use it. */
export const INTENT_CHECKS = ["bad", "keep", "uncertain"] as const;
export type IntentCheck = (typeof INTENT_CHECKS)[number];

/** One check result on an ad or post. Lives here (a leaf module) so social.ts can use it without an import cycle. */
export const adProblemSchema = z.object({ rule: z.string(), message: z.string(), severity: z.enum(["fix", "advice"]), where: z.string(), flagged: z.boolean().optional() });
